/* eslint-disable react/no-find-dom-node */
import React, { useRef, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
export interface IframeMessageData {
    type: string;
    data?: Record<string, unknown>;
}

interface TrustedIframeProps extends React.HTMLAttributes<HTMLIFrameElement> {
    autoHeight?: boolean;
    content?: string;
    src?: string;
    minHeight?: number;
    extraMargin?: number;
    fullScreen?: boolean;
    noSandbox?: boolean;
}

const useStyles = makeStyles(() => ({
    iframeClass: {
        width: '100%',
        fontFamily: 'Arial',
        border: 0,
        overflowY: 'hidden',
        height: '35vw',
        '& img': {
            maxWidth: '100%',
            height: 'auto',
        },
        '& blockquote': {
            display: 'none',
        },
    },
    fullScreen: {
        height: '100%',
    },
}));

export const TrustedIframe: React.FC<TrustedIframeProps> = ({
    autoHeight,
    content,
    src,
    className,
    fullScreen,
    noSandbox,
    minHeight = 1000000,
    extraMargin = 0,
    ...props
}) => {
    const classes = useStyles();
    const [height, setHeight] = useState(100);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Listen for postMessage events from iframe
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Only process messages from our iframe
            if (iframeRef.current?.contentWindow !== event.source) {
                return;
            }

            if (event.data.type === 'MILJOPROFIL_READY') {
                // Send parent identifier to child iframe
                iframeRef.current?.contentWindow?.postMessage(
                    {
                        type: 'MILJOPROFIL_PARENT_ID',
                        parent: 'kundeportal',
                    },
                    '*',
                );
            }

            const messageHeight = event.data.height;

            if (autoHeight && typeof messageHeight === 'number') {
                const newHeight = Math.min(
                    messageHeight + extraMargin,
                    minHeight,
                );
                setHeight(newHeight);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [autoHeight, extraMargin, minHeight]);

    const iframeHeight = autoHeight ? height : '100%';

    const classNames = classnames(classes.iframeClass, className, {
        [classes.fullScreen]: fullScreen,
    });

    const trustedSandbox = [
        'allow-same-origin',
        'allow-popups',
        'allow-popups-to-escape-sandbox',
        'allow-scripts',
        'allow-forms',
        'allow-modals',
        'allow-downloads',
        'allow-top-navigation',
    ].join(' ');

    return (
        <iframe
            ref={iframeRef}
            title="trustedIframe"
            srcDoc={content}
            src={src}
            className={classNames}
            style={{
                height: iframeHeight,
            }}
            sandbox={noSandbox ? undefined : trustedSandbox}
            data-testid={props['data-testid']}
        />
    );
};
